import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentityBodyAssess, IdentityPerson } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save team data.
 */
@Injectable()
export class IdentityBodyAssessService {
    public static INCLUDE_READINGS: string = 'readings';
    public static INCLUDE_SEASON: string = 'season';
    public static INCLUDE_PERSON: string = 'person';

    private readonly identityApiBaseUrl: string;
    private cachedMap: { [apiRequest: string]: IdentityBodyAssess[]; } = {};
    private cachedBodyAssess: { [apiRequest: string]: IdentityBodyAssess; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    public clearCache(): void {
        this.cachedMap = {};
        this.cachedBodyAssess = {};
    }

    /**
     * Get all readings for a given body assessment
     */
    public getIndex(
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityBodyAssess[]> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);

        if (this.cachedMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedMap[apiRequestUrl].map((a: IdentityBodyAssess) => a.clone()),
            );
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const bodyAssessments: IdentityBodyAssess[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const assessment of bodyData) {
                    bodyAssessments.push((new IdentityBodyAssess()).deserialize(assessment, included));
                }

                this.cachedMap[apiRequestUrl] = bodyAssessments;
                return bodyAssessments.map((i: IdentityBodyAssess) => i.clone());
            }));
    }

    /**
     * Get a single bodyAssessmentReading
     */
    public get(
        identityBodyAssessmentId: string,
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<IdentityBodyAssess> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(identityBodyAssessmentId, includes, queryParams);

        if (!forceRefresh && this.cachedBodyAssess[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedBodyAssess[apiRequestUrl].clone());
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const bodyAssess: IdentityBodyAssess = (new IdentityBodyAssess().deserialize(response));
                this.cachedBodyAssess[apiRequestUrl] = bodyAssess.clone();
                return bodyAssess;
            }));
    }

    /**
     * Calculate the body fat and minimum weight for the given assessment
     */
    public calculateBodyFat(
        bodyAssessmentId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityBodyAssess> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(`${bodyAssessmentId}/calculate-body-fat`, includes, queryParams);

        // Make the http request
        return this.http.patch(apiRequestUrl, null)
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityBodyAssess().deserialize(response))),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Add or update a bodyAssessmentReading
     */
    public save(
        bodyAssessment: IdentityBodyAssess,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityBodyAssess> {
        let response: Observable<IJsonApiResponse>;
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(bodyAssessment.id, includes, queryParams);

        if (bodyAssessment.id) {
            // Update existing BodyAssessment
            response = this.http.patch(apiRequestUrl, bodyAssessment.serialize());
        } else {
            // Add a new BodyAssessment
            response = this.http.post(apiRequestUrl, bodyAssessment.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityBodyAssess()).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified body assessment
     */
    public delete(bodyAssessmentId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(bodyAssessmentId);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.http.clearCache(IdentityBodyAssess)),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        bodyAssessmentId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.identityApiBaseUrl}/body-assessments/`;

        if (bodyAssessmentId) {
            url += bodyAssessmentId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[person]') !== -1).length === 0) {
            queryParams.push(`fields[person]=${IdentityPerson.getFields().join(',')}`);
        }

        return queryParams;
    }
}

